import { z } from 'zod';

export const planIntervalSchema = z.enum(['month', 'year', 'lifetime']);
export type PlanInterval = z.infer<typeof planIntervalSchema>;

export const planFeatureSchema = z.object({
  key: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z][a-z0-9_]*$/, 'snake_case zorunlu'),
  label: z.string().min(1).max(120),
  included: z.boolean().default(true),
});
export type PlanFeature = z.infer<typeof planFeatureSchema>;

const slugSchema = z
  .string()
  .min(3)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'Slug yalnızca küçük harf, rakam ve alt çizgi içermeli');

const baseCreateShape = {
  slug: slugSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(500).nullable().optional(),
  interval: planIntervalSchema,
  amountEurCents: z.coerce.number().int().nonnegative().max(99_999_99),
  trialDays: z.coerce.number().int().min(0).max(365).default(0),
  features: z.array(planFeatureSchema).max(40).default([]),
  isPublic: z.boolean().default(true),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
};

export const adminCreatePlanSchema = z
  .object(baseCreateShape)
  .refine((d) => d.interval !== 'lifetime' || d.trialDays === 0, {
    message: 'Lifetime planlar için trial gün olamaz',
    path: ['trialDays'],
  });
export type AdminCreatePlanInput = z.infer<typeof adminCreatePlanSchema>;

// Stripe Product/Price ID format'ı:
//   prod_ABC...  (Product, 14+ char alphanumeric)
//   price_ABC... (Price,   14+ char alphanumeric, _ dahil)
const stripeProductIdSchema = z
  .string()
  .trim()
  .regex(/^prod_[A-Za-z0-9]+$/, "Stripe Product ID 'prod_' ile başlamalı")
  .nullable()
  .optional();

const stripePriceIdSchema = z
  .string()
  .trim()
  .regex(/^price_[A-Za-z0-9]+$/, "Stripe Price ID 'price_' ile başlamalı (prod_ değil)")
  .nullable()
  .optional();

export const adminUpdatePlanSchema = z
  .object({
    name: baseCreateShape.name.optional(),
    description: baseCreateShape.description,
    interval: baseCreateShape.interval.optional(),
    amountEurCents: baseCreateShape.amountEurCents.optional(),
    trialDays: baseCreateShape.trialDays.optional(),
    features: baseCreateShape.features.optional(),
    isPublic: z.boolean().optional(),
    isActive: z.boolean().optional(),
    sortOrder: baseCreateShape.sortOrder.optional(),
    // Stripe ID'leri manuel override için (örn. Stripe'ta zaten oluşturulmuş
    // Product+Price'ı sisteme bağlamak için). Boş string null'a düşer.
    stripeProductId: stripeProductIdSchema,
    stripePriceId: stripePriceIdSchema,
  })
  .refine(
    (d) => d.interval === undefined || d.interval !== 'lifetime' || (d.trialDays ?? 0) === 0,
    {
      message: 'Lifetime planlar için trial gün olamaz',
      path: ['trialDays'],
    },
  );
export type AdminUpdatePlanInput = z.infer<typeof adminUpdatePlanSchema>;

export const listPlansQuerySchema = z.object({
  includeInactive: z.coerce.boolean().optional().default(false),
  includeDeleted: z.coerce.boolean().optional().default(false),
});
export type ListPlansQuery = z.infer<typeof listPlansQuerySchema>;

export const planResponseSchema = z.object({
  id: z.string().uuid(),
  slug: slugSchema,
  name: z.string(),
  description: z.string().nullable(),
  interval: planIntervalSchema,
  amountEurCents: z.number().int().nonnegative(),
  currency: z.string().length(3),
  trialDays: z.number().int().min(0),
  features: z.array(planFeatureSchema),
  isActive: z.boolean(),
  isPublic: z.boolean(),
  stripeProductId: z.string().nullable(),
  stripePriceId: z.string().nullable(),
  sortOrder: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type PlanResponse = z.infer<typeof planResponseSchema>;
